import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, FormErrorUI, DatagridPremiumUi, InputUI, ButtonUI } from '../Interface';

import documentService from '../../service/newDoc.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const BatchOut = ({ disabled, selectedLine, defaultBatchLine, onClose }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [batches, setBatches] = useState([]);

  const getBatches = () => {
    documentService
      .getBatches({
        warehouseCode: values.whsCode,
        itemCode: values.docLine[selectedLine.selectedLineIndex]?.itemCode,
      })
      .then((data) => {
        setBatches(data);
      });
  };

  useEffect(() => {
    if (!values.whsCode) {
      addNotification({
        content: 'Nincs megadott raktár!',
        type: NotificationType.WARNING,
      });
    }
    if (!values.docLine?.[selectedLine?.selectedLineIndex]?.itemCode) return;

    getBatches();
  }, [values.whsCode, values.docLine?.[selectedLine?.selectedLineIndex]?.itemCode]);

  if (!selectedLine || typeof selectedLine.selectedLineIndex !== 'number') return null;
  const { selectedLineIndex } = selectedLine;

  const getBatchIndex = (docLineBatchSn, id) => {
    return docLineBatchSn.findIndex((rc) => rc.id === id);
  };

  const handleAddClick = async (row, push) => {
    defaultBatchLine.id = uuidv4();
    defaultBatchLine.batchSn = row.batchSn;
    await push(defaultBatchLine);
    apiRef.current.startRowEditMode({ id: defaultBatchLine.id });

    const fieldsToValidate = [
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.batchSn`,
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.quantity`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  const columns = (push) => {
    return dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddClick(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
        sortable: false,
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
      {
        field: 'lot',
        headerName: 'LOT',
        type: 'string',
        sortable: false,
      },
      {
        field: 'supplierId',
        headerName: 'Beszállító azon.',
        type: 'string',
        sortable: false,
      },
      {
        field: 'expDate',
        headerName: 'Lejárat',
        type: 'date',

        sortable: false,
        valueGetter: (_data, row) => {
          return row.expDate ? new Date(row.expDate) : null;
        },
        renderCell: (data) => {
          return data.row.expDate ? dayjs(data.row.expDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'mnfDate',
        headerName: 'Gyártás',
        type: 'date',
        sortable: false,
        valueGetter: (_data, row) => {
          return row.mnfDate ? new Date(row.mnfDate) : null;
        },
        renderCell: (data) => {
          return data.row.mnfDate ? dayjs(data.row.mnfDate).format('YYYY.MM.DD') : '';
        },
      },
      {
        field: 'batchAttribute1',
        headerName: 'Attribútum 1',
        type: 'string',
        sortable: false,
      },
      {
        field: 'batchAttribute2',
        headerName: 'Attribútum 2',
        type: 'string',
        sortable: false,
      },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        sortable: false,
      },
      {
        field: 'price',
        headerName: 'Ár',
        type: 'string',
        sortable: false,
        valueGetter: (data, row) => {
          return row.price ?? '';
        },
      },
      {
        field: 'other',
        headerName: 'Egyéb',
        type: 'string',
        sortable: false,
      },
    ]);
  };

  const batchOutCols = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        editable: true,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity} />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`)
              }
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, id);
                return remove(batchIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  const handleSave = () => {
    onClose();
  };

  const handleCancel = () => {
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBatchSn`, selectedLine.docLineBatchSn || []);
    onClose();
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box className="align-middle">
        <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
            <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Sor: </Typography>
              <Typography>{selectedLineIndex + 1}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Cikkszám: </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.itemCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Nyitott mennyiség: </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.qty ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Összesített sarzs: </Typography>
              <Typography>
                {(values.docLine[selectedLineIndex]?.docLineBatchSn ?? []).reduce(
                  (sum, batch) => sum + (Number(batch.quantity) || 0),
                  0
                )}
              </Typography>
            </Box>
          </Box>

          <FieldArray name={`docLine.${selectedLineIndex}.docLineBatchSn`}>
            {({ remove, push }) => (
              <>
                <Box display="flex" gap={2}>
                  <Box flex={1} minWidth={0}>
                    <DatagridPremiumUi
                      datagridIdentifier={DataGridIdentifiers.BatchInStockTable}
                      height={480}
                      columns={columns(push)}
                      rows={batches ?? []}
                      slots={{
                        toolbar: null,
                      }}
                      onCellDoubleClick={(_params, event) => {
                        event.defaultMuiPrevented = true;
                      }}
                      headerFilters={false}
                      getRowHeight={() => 'auto'}
                      getRowId={(row) => row.batchSnId}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <DatagridPremiumUi
                      apiRef={apiRef}
                      datagridIdentifier={DataGridIdentifiers.BatchOutTable}
                      height={480}
                      columns={batchOutCols(setFieldValue, validateField, errors, remove)}
                      rows={values.docLine[selectedLineIndex]?.docLineBatchSn ?? []}
                      editMode="row"
                      onCellDoubleClick={(_params, event) => {
                        event.defaultMuiPrevented = true;
                      }}
                      slots={{
                        toolbar: null,
                      }}
                      headerFilters={false}
                      getRowHeight={() => 'auto'}
                      onCellClick={(params, event) => {
                        if (disabled) return;
                        const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                        for (const rowId of rowsInEditMode) {
                          if (params.row.id.toString() !== rowId) {
                            apiRef.current.stopRowEditMode({ id: rowId });
                          }
                        }

                        const rowMode = apiRef.current.getRowMode(params.row.id);
                        if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
                  <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
                </Box>
              </>
            )}
          </FieldArray>
        </Paper>
      </Box>
    </Box>
  );
};

export default BatchOut;
